<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

import GoogleSheetImportsAPI from 'dashboard/api/googleSheetImports.js';
import GoogleSheetImportItem from './GoogleSheetImportItem.vue';
import GoogleSheetImportModal from './GoogleSheetImportModal.vue';

const { t } = useI18n();
const imports = ref([]);
const isLoading = ref(false);
const showModal = ref(false);
const editingItem = ref(null);

const fetchImports = async () => {
  isLoading.value = true;
  try {
    const { data } = await GoogleSheetImportsAPI.get();
    imports.value = data;
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.API.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openCreateModal = () => {
  editingItem.value = null;
  showModal.value = true;
};

const openEditModal = item => {
  editingItem.value = item;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingItem.value = null;
};

const handleSave = async payload => {
  try {
    if (payload.id) {
      await GoogleSheetImportsAPI.update(payload.id, payload);
      useAlert(t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.API.UPDATE_SUCCESS'));
    } else {
      await GoogleSheetImportsAPI.create(payload);
      useAlert(t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.API.CREATE_SUCCESS'));
    }
    closeModal();
    fetchImports();
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.API.SAVE_ERROR'));
  }
};

const handleDelete = async item => {
  if (!window.confirm(t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.DELETE_CONFIRM'))) {
    return;
  }
  try {
    await GoogleSheetImportsAPI.delete(item.id);
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.API.DELETE_SUCCESS'));
    fetchImports();
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.API.DELETE_ERROR'));
  }
};

const handleSync = async importId => {
  try {
    await GoogleSheetImportsAPI.sync(importId);
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.API.SYNC_SUCCESS'));
    fetchImports();
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.API.SYNC_ERROR'));
  }
};

onMounted(fetchImports);
</script>

<template>
  <div class="border-t border-n-weak p-6 bg-n-alpha-1 rounded-b-md">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11 mt-0.5">
          {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        sm
        blue
        :label="t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.ADD_NEW')"
        icon="i-lucide-plus"
        @click="openCreateModal"
      />
    </div>

    <div v-if="isLoading" class="text-center py-8 text-n-slate-11 text-sm">
      {{ t('INTEGRATION_SETTINGS.LOADING') }}...
    </div>

    <div v-else-if="imports.length === 0" class="text-center py-8 text-n-slate-11 text-sm">
      {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.EMPTY') }}
    </div>

    <div v-else class="flex flex-col gap-3">
      <GoogleSheetImportItem
        v-for="item in imports"
        :key="item.id"
        :import-item="item"
        @edit="openEditModal"
        @delete="handleDelete"
        @sync="handleSync"
      />
    </div>

    <GoogleSheetImportModal
      :show="showModal"
      :import-item="editingItem"
      @close="closeModal"
      @save="handleSave"
    />
  </div>
</template>
